import { Stack, Text, mergeStyles, keyframes } from "@fluentui/react";
import { useState, useCallback, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { HorizontalStack, VerticalStack } from "../../Stack";
import { useTranslation } from "react-i18next";
import { BtFileObject, Document } from "../../../models";
import CrossOutlinedIcon from "../../../assets/Icons/svg/crossOutlineIcon";
import WordDocIcon from "../../../assets/Icons/svg/wordDocIcon";
import PdfIconAlternative from "../../../assets/Icons/svg/pdfIconAlternative";
import CustomDropdown from "../../CustomDropDown";
import { colorPallete } from "../../../assets/color";

interface FileCardProps {
  fileObject: BtFileObject;
  onRemove: () => void;
  onSave: any;
  isInvalid?: boolean;
  docsData?: Document[];
  onFileTypeChange?: (
    isNewVersion: boolean,
    rootDocumentId?: number,
    documentFamilyName?: string
  ) => void;
}

const FileCardStackStyle = mergeStyles({
  backgroundColor: "",
  gap: "8px",
  alignItems: "flex-start",
  width: "100%",
});

const newFileNameTextStyle = mergeStyles({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: "Poppins",
  color: "#1F2430",
  fontSize: "13px",
  fontWeight: "400",
  lineHeight: "18px",
  width: "100%",
});

const slideDownEnter = keyframes({
  "0%": { opacity: 0, transform: "translateY(-6px)" },
  "100%": { opacity: 1, transform: "translateY(0)" },
});

const dropdownEnterStyle = mergeStyles({
  animation: `${slideDownEnter} 0.22s ease forwards`,
});

const smallDropdownStyle = mergeStyles({
  selectors: {
    " .dropDownText": { fontSize: "13px" },
    " .dropdownCard": { fontSize: "13px", minWidth: "unset" },
    " .versionText": { fontSize: "11px" },
    " .versionTextSelect": { fontSize: "11px" },
    " .dropDownContainer": { backgroundColor: "white" },
    
  },
});

const toggleLabelStyle = {
  fontFamily: "Poppins",
  fontSize: "10px",
  fontWeight: "500" as const,
  whiteSpace: "nowrap" as const,
  lineHeight: "14px",
};

const FileCard: React.FC<FileCardProps> = ({
  fileObject,
  onRemove,
  onSave,
  isInvalid = false,
  docsData,
  onFileTypeChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newFileName, setNewFileName] = useState(fileObject.fileName);
  const [selectedDocItem, setSelectedDocItem] = useState<Document | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useTranslation();

  const isNewVersion = fileObject.isNewVersion ?? false;

  const stableDocsData = useMemo(() => docsData ?? [], [docsData]);

  const handleSetDocumentItem = useCallback((item: Document | null) => {
    setSelectedDocItem(item);
    if (item) {
      onFileTypeChange?.(
        true,
        item.rootDocumentId,
        item.rootDocumentName?.split(".docx")[0] ?? ""
      );
    }
  }, [onFileTypeChange]);

  // A file can arrive already pointing at a document family (the modal was
  // opened from that document's import icon), so mirror that target into the
  // dropdown selection as soon as the project documents are available.
  useEffect(() => {
    if (!isNewVersion || !fileObject.rootDocumentId) return;
    if (selectedDocItem?.rootDocumentId === fileObject.rootDocumentId) return;
    const preselected = stableDocsData.find(
      (doc: any) => doc?.rootDocumentId === fileObject.rootDocumentId
    );
    if (preselected) setSelectedDocItem(preselected);
  }, [isNewVersion, fileObject.rootDocumentId, stableDocsData, selectedDocItem]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewFileName(event.target.value);
  };

  const handleSave = () => {
    if (!newFileName) {
      setNewFileName(fileObject.fileName);
    }
    const updatedName = onSave(newFileName);
    if (updatedName) {
      setNewFileName(updatedName);
    }
  };

  const handleToggle = (toNewVersion: boolean) => {
    if (toNewVersion === isNewVersion) return;
    if (!toNewVersion) {
      setSelectedDocItem(null);
      onFileTypeChange?.(false, undefined, undefined);
    } else {
      onFileTypeChange?.(true, undefined, undefined);
    }
  };

  const isWordDoc = fileObject.fileName.toLowerCase().includes(".doc");

  return (
    <VerticalStack
      style={{
        border: "1px solid",
        borderRadius: "5px",
        borderColor: isInvalid ? colorPallete.btRed : "#D8E1EB",
        width: "100%",
        padding: isNewVersion ? "10px" : "5px 10px",
        gap: isNewVersion ? "4px" : "0px",
        background: "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
        position: "relative",
        zIndex: isDropdownOpen ? 10 : "auto",
      }}
    >
      {/* Row 1: icon + filename + toggle + X */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
          alignItems: "center",
          minHeight: "32px",
          gap: "6px",
          width: "100%",
          padding: "5px 10px",
          borderRadius: "5px",
          background: isNewVersion
          ? "#fff"
          : "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
        }}
      >
        <Stack style={{ flexShrink: 0 }}>
          {isWordDoc ? <WordDocIcon /> : <PdfIconAlternative />}
        </Stack>
        <Stack style={{ minWidth: 0 }}>
          {isEditing ? (
            <input
              title={newFileName}
              type="text"
              value={newFileName}
              onChange={handleInputChange}
              onBlur={() => {
                handleSave();
                setIsEditing(false);
              }}
              style={{ fontFamily: "Poppins", width: "95%", minWidth: 0, boxSizing: "border-box", fontSize: "13px", backgroundColor: "white", paddingRight: "5px" }}
              autoFocus={true}
            />
          ) : (
            <Text
              block
              className={newFileNameTextStyle}
              title={newFileName}
              onClick={() => setIsEditing(true)}
            >
              {newFileName}
            </Text>
          )}
        </Stack>
        {/* Segmented toggle with sliding pill */}
        <div
          style={{
            position: "relative",
            display: "flex",
            border: "1px solid #D0D0D0",
            borderRadius: "50px",
            overflow: "hidden",
            flexShrink: 0,
            height: "22px",
            backgroundColor: "#F5F5F5",
          }}
        >
          {/* Sliding pill */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "54%",
              height: "100%",
              borderRadius: "50px",
              backgroundColor: colorPallete.btBlue,
              transform: isNewVersion ? "translateX(100%)" : "translateX(0%)",
              transition: "transform 0.2s ease",
              pointerEvents: "none",
            }}
          />
          {/* New Document label */}
          <div
            onClick={() => handleToggle(false)}
            style={{
              position: "relative",
              zIndex: 1,
              padding: "0px 8px 2px 8px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Text style={{ ...toggleLabelStyle, color: !isNewVersion ? "#fff" : "#777", transition: "color 0.2s ease" }}>
              {t("new_document")}
            </Text>
          </div>
          {/* New Version label */}
          <div
            onClick={() => handleToggle(true)}
            style={{
              position: "relative",
              zIndex: 1,
              padding: "0px 8px 2px 8px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Text style={{ ...toggleLabelStyle, color: isNewVersion ? "#fff" : "#777", transition: "color 0.2s ease" }}>
              {t("new_version")}
            </Text>
          </div>
        </div>
        <Stack
          onClick={onRemove}
          title={String(t("remove"))}
          style={{ cursor: "pointer", flexShrink: 0 }}
        >
          <CrossOutlinedIcon />
        </Stack>
      </div>

      {/* Row 2: document family dropdown */}
      {isNewVersion && (
        <div className={dropdownEnterStyle}>
          <Stack className={smallDropdownStyle} style={{ paddingBottom: "4px" }}>
            <span style={{ fontFamily: "Poppins", color: "#1F2430", margin: "4px 0 4px" }}>
              {t("document_family")}:
            </span>
            <CustomDropdown
              options={stableDocsData}
              title={t("document_dd")}
              isDocumentDropdown={true}
              setDocumentItem={handleSetDocumentItem}
              documentItem={selectedDocItem}
              rootDocumentId={fileObject.rootDocumentId ?? 0}
              onOpenChange={setIsDropdownOpen}
            />
          </Stack>
        </div>
      )}
    </VerticalStack>
  );
};

interface DocumentListProps {
  multipleFiles: BtFileObject[];
  setMultipleFiles: Dispatch<SetStateAction<BtFileObject[]>>;
  docsData?: Document[];
  invalidFiles?: { [key: string]: boolean };
  onFileNameChange?: (fileName: string, oldFileName: string) => void;
  onFileTypeChange?: (
    fileName: string,
    isNewVersion: boolean,
    rootDocumentId?: number,
    documentFamilyName?: string
  ) => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  multipleFiles,
  setMultipleFiles,
  docsData,
  invalidFiles = {},
  onFileNameChange,
  onFileTypeChange,
}) => {
  const handleRemove = (file: BtFileObject) => {
    setMultipleFiles((prevFiles) =>
      prevFiles.filter((current) => current.fileName !== file.fileName)
    );
  };

  function updateFileNameWithExtension(name: string, filename: string) {
    if (!name) {
      name = filename;
    }

    const lowerFilename = filename.toLowerCase();
    if (lowerFilename.endsWith(".doc")) {
      if (!name.toLowerCase().endsWith(".doc")) {
        return name + ".doc";
      }
    } else if (lowerFilename.endsWith(".docx")) {
      if (!name.toLowerCase().endsWith(".docx")) {
        return name + ".docx";
      }
    } else if (lowerFilename.endsWith(".pdf")) {
      if (!name.toLowerCase().endsWith(".pdf")) {
        return name + ".pdf";
      }
    }
    return name;
  }

  const lowercaseAfterLastDot = (text: string) => {
    const lastDotIndex = text.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === text.length - 1) {
      return text;
    }

    const beforeDot = text.slice(0, lastDotIndex + 1);
    const afterDot = text.slice(lastDotIndex + 1);

    return beforeDot + afterDot.toLowerCase();
  };

  const handleSave = (fileObject: BtFileObject, name: string) => {
    const newFileName = updateFileNameWithExtension(name, fileObject.file.name);
    let document: BtFileObject = {
      ...fileObject,
      fileName: lowercaseAfterLastDot(newFileName),
    };
    let copyArray = [...multipleFiles];
    const index = multipleFiles.findIndex(
      (docObject) => docObject.fileName === fileObject.fileName
    );
    if (index !== -1) {
      copyArray.splice(index, 1, document);
      setMultipleFiles(copyArray);
      onFileNameChange?.(document.fileName, fileObject.fileName);
      return document.fileName;
    }
    return null;
  };

  const handleFileTypeChange = (
    fileName: string,
    isNewVersion: boolean,
    rootDocumentId?: number,
    documentFamilyName?: string
  ) => {
    setMultipleFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.fileName === fileName
          ? {
              ...f,
              isNewVersion,
              rootDocumentId: isNewVersion ? rootDocumentId : undefined,
              documentFamilyName: isNewVersion ? documentFamilyName : undefined,
            }
          : f
      )
    );
    onFileTypeChange?.(fileName, isNewVersion, rootDocumentId, documentFamilyName);
  };

  return (
    <VerticalStack className={FileCardStackStyle}>
      {multipleFiles &&
        multipleFiles.map((file) => (
          <FileCard
            key={file.fileName}
            fileObject={file}
            onRemove={() => handleRemove(file)}
            onSave={(fileName: string) => handleSave(file, fileName)}
            isInvalid={invalidFiles[file.fileName]}
            docsData={docsData}
            onFileTypeChange={(isNewVersion, rootDocumentId, documentFamilyName) =>
              handleFileTypeChange(file.fileName, isNewVersion, rootDocumentId, documentFamilyName)
            }
          />
        ))}
    </VerticalStack>
  );
};

export default DocumentList;
